package com.bitterinfant.laura

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.bitterinfant.shared.*
import com.bitterinfant.webui.AudioPlayer
import com.bitterinfant.webui.BannerImage
import com.bitterinfant.webui.DeepLink
import com.bitterinfant.webui.ImageBanner
import com.bitterinfant.webui.PageShell
import com.bitterinfant.webui.Site
import com.bitterinfant.webui.TABS_HEIGHT_PX
import com.bitterinfant.webui.Tabs
import com.bitterinfant.webui.Theme
import com.bitterinfant.webui.VideoPlayer
import com.bitterinfant.webui.YouTubeEmbed
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable

fun main() {
    renderComposable(rootElementId = "root") {
        App()
    }
}

// ---------------------------------------------------------------------------------------------
// Content. Everything below is data — fill these in as media becomes available; any empty list
// renders a "Coming soon" line in its tab instead of broken players.
// ---------------------------------------------------------------------------------------------

/** One line in the banner's links block, rendered as "[leadIn] [label]" with [label] linked. */
private class HeroLink(val leadIn: String, val label: String, val url: String)

/** Banner links, one per line, e.g. HeroLink("Credits on", "IMDb", "https://www.imdb.com/name/…"). */
private val heroLinks = listOf<HeroLink>(
)

/** A video on the Acting tab: either a YouTube embed or a self-hosted file. */
private sealed interface Clip {
    /** [videoId] is the part after `v=` in a youtube.com/watch URL. */
    class YouTube(val videoId: String, val caption: String? = null, val tooltip: String? = null) : Clip
    /** Self-hosted: drop files in src/jsMain/resources/media/ (webm first, mp4 fallback). */
    class Hosted(val item: MediaItem) : Clip
}

private val actingClips = listOf<Clip>(
    // Clip.YouTube("VIDEO_ID", caption = "Acting Demo Reel"),
    // Clip.Hosted(MediaItem(
    //     title = "Acting Demo Reel",
    //     sources = listOf(
    //         MediaSource("/media/laura-acting-reel.webm", "video/webm", MediaKind.VIDEO),
    //         MediaSource("/media/laura-acting-reel.mp4", "video/mp4", MediaKind.VIDEO),
    //     ),
    //     posterPath = "/media/laura-acting-reel-poster.jpg",
    // )),
)

/** A VO demo: [path] is a URL or a /media/… path; [tooltip] is hover-only info text. */
private class Demo(val title: String, val path: String, val mimeType: String = "audio/mpeg", val tooltip: String? = null)

private val voDemos = listOf<Demo>(
    // Demo("Commercial VO Demo", "/media/laura-vo-commercial.mp3"),
    // Demo("Narration VO Demo", "/media/laura-vo-narration.mp3"),
)

private val resume = listOf<ResumeEntry>(
    // ResumeEntry("Voice Over Artist / Actor", "Freelance", "20XX — present",
    //     listOf("Commercial, narration, and character VO", "On-camera and stage acting credits")),
)

private val tabLabels = listOf("Acting", "Voice Over", "Resume")

/**
 * Banner photo per tab, indexed to match [tabLabels]. Until a photo exists at a path the banner
 * shows a plain dark background. Once real photos are in, tune placement per photo the same way
 * as site-matthew's `bannerImages` (e.g. heightPercent = 130, verticalPercent = 10, focusX = 75
 * to pin the subject right and fade the photo's left edge into the gradient).
 */
private val bannerImages = listOf(
    BannerImage("/media/banner-acting.jpg"),  // Acting
    BannerImage("/media/banner-vo.jpg"),      // Voice Over
    BannerImage("/media/banner-resume.jpg"),  // Resume
)

// ---------------------------------------------------------------------------------------------

@Composable
fun App() {
    val initialTab = remember { DeepLink.tabIndex(tabLabels) }
    var activeTab by remember { mutableStateOf(initialTab) }
    PageShell(
        current = Site.LAURA,
        title = "Laura Steele",
        tagline = "Actor, VO, Producer",
        // No header bar: the title/tagline live inside the banner instead, so the photo and
        // its gradient run all the way to the top of the page with no dividing line.
        showHeader = false,
    ) {
        Section(attrs = { style { marginBottom(0.px) } }) {
            val banner = bannerImages[activeTab]
            ImageBanner(
                image = banner,
                imageAlt = "",
                flushTop = true,
            ) {
                H1(attrs = { style { margin(0.px); fontSize(1.6.cssRem) } }) { Text("Laura Steele") }
                P(attrs = { style { margin(0.px); color(Color(Theme.textMuted)) } }) { Text("Actor, VO, Producer") }
                // Equal top/bottom margins center the links in the gap between the tagline
                // and the tab strip; with no links yet, an empty block keeps the same spacing.
                P(attrs = { style { marginTop(3.cssRem); marginBottom(3.cssRem) } }) {
                    heroLinks.forEachIndexed { i, link ->
                        if (i > 0) Br()
                        Text("${link.leadIn} ")
                        A(href = link.url, attrs = { attr("target", "_blank") }) { Text(link.label) }
                        Text(if (i == heroLinks.lastIndex) "." else ",")
                    }
                }
            }
        }
        // Pulls the tab strip up over the bottom of the banner photo; position: relative puts
        // it in the same paint layer as the banner so it draws on top.
        Div(attrs = { style { position(Position.Relative); marginTop((-TABS_HEIGHT_PX).px) } }) {
            Tabs(labels = tabLabels, initial = initialTab, onSelect = { activeTab = it }) { active ->
                when (active) {
                    0 -> Section {
                        if (actingClips.isEmpty()) ComingSoon()
                        actingClips.forEach { clip ->
                            when (clip) {
                                is Clip.YouTube -> YouTubeEmbed(clip.videoId, caption = clip.caption, tooltip = clip.tooltip)
                                is Clip.Hosted -> VideoPlayer(clip.item)
                            }
                        }
                    }
                    1 -> Section {
                        if (voDemos.isEmpty()) ComingSoon()
                        voDemos.forEach { demo ->
                            AudioPlayer(
                                MediaItem(
                                    title = demo.title,
                                    sources = listOf(MediaSource(demo.path, demo.mimeType, MediaKind.AUDIO)),
                                ),
                                tooltip = demo.tooltip,
                            )
                        }
                    }
                    else -> Section {
                        if (resume.isEmpty()) ComingSoon()
                        resume.forEach { entry ->
                            Div(attrs = { style { marginBottom(1.2.cssRem) } }) {
                                H3(attrs = { style { marginBottom(0.px) } }) { Text(entry.heading) }
                                P(attrs = { style { marginTop(0.px); color(Color(Theme.textMuted)) } }) { Text("${entry.subheading} · ${entry.period}") }
                                Ul { entry.bullets.forEach { Li { Text(it) } } }
                            }
                        }
                        H3 { Text("Contact") }
                        P { Text("Booking & inquiries via Bitter Infant Productions.") }
                    }
                }
            }
        }
    }
}

@Composable
private fun ComingSoon() {
    P(attrs = { style { color(Color(Theme.textMuted)) } }) { Text("Coming soon.") }
}
